import { waitForNosto, requestNostoReco, fetchNostoCards, attributeRecoClicks } from '@theme/nosto-reco-core';

/**
 * <nosto-recommendations-sidecart> — reco Nosto du cart drawer.
 *
 * Chargé une seule fois en tant que module depuis sections/cart-drawer.liquid
 * (et non via un <script> inline dans le snippet) : le custom element doit être
 * défini globalement pour s'upgrader automatiquement quand le drawer passe de
 * l'état vide à rempli via morph. Un <script> injecté par morph/innerHTML ne
 * s'exécute jamais → l'élément resterait inerte (skeleton figé).
 *
 * Le flux reco (placement → cartes) est factorisé dans `@theme/nosto-reco-core`,
 * partagé avec la reco PDP et l'onglet Nosto de ax-tabbed-product-lists. Ici on ne
 * garde que la couche spécifique sidecart : carrousel horizontal + flèches.
 */
if (!customElements.get('nosto-recommendations-sidecart')) {
  class NostoRecommendationsSidecart extends HTMLElement {
    static SECTION_NAME = 'card-product-sidecart-nosto';
    static MAX_PRODUCTS = 3;

    constructor() {
      super();
      this.placement = this.dataset.nostoPlacement;
      this._wired = false; // listeners posés une fois
      this._loaded = false; // une reco a ABOUTI → figée (plus de re-fetch : pas de churn)
      this._loading = false; // requête en vol → anti double-fetch
    }

    // Lazy : on charge la reco à la PREMIÈRE ouverture du drawer (cf. sidecart.md), pas au
    // page load. Booter en connectedCallback chargeait la reco avec le panier de l'instant —
    // souvent VIDE (arrivée panier vide) → zero-products → masquée à vie (l'élément n'est
    // jamais recréé : `data-skip-subtree-update` sur la racine). On (re)tente donc à CHAQUE
    // ouverture tant qu'aucune reco n'a abouti ; dès qu'une reco aboutit, `_loaded` fige le
    // résultat. On NE re-fetch jamais sur cart:update : sinon chaque ajout/modif re-swappait
    // toute la liste → la carte cliquée disparaissait + churn visuel.
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      document.addEventListener('ax:cart-drawer-opened', () => this.maybeLoad());

      // Design mode (le drawer peut être déjà ouvert après un section refresh) ou drawer
      // déjà ouvert à la connexion (auto-open redirect_cart_page) → charger tout de suite.
      if (window.Shopify?.designMode || this.closest('dialog')?.open) this.maybeLoad();
    }

    async maybeLoad() {
      if (this._loaded || this._loading) return;
      this._loading = true;
      try {
        await waitForNosto();
      } catch (err) {
        this._loading = false;
        this.hide('nosto-timeout');
        return;
      }
      this.requestRecommendations();
    }

    requestRecommendations() {
      this.removeAttribute('data-nosto-empty');
      const root = this.closest('[data-nosto-sidecart-root]');
      if (root) root.removeAttribute('data-nosto-sidecart-empty');

      requestNostoReco(this.placement)
        .then((products) => {
          this._loading = false;
          // Rien à afficher (panier vide / placement vide) → on NE fige PAS : on retentera
          // à la prochaine ouverture du drawer (le panier aura alors ses articles).
          if (!Array.isArray(products) || products.length === 0) return this.hide('zero-products');
          this._loaded = true; // succès → figé, plus de re-fetch
          this.buildAndAttach(products);
        })
        .catch(() => {
          this._loading = false;
          this.hide('api-error');
        });
    }

    async buildAndAttach(products) {
      const valid = await fetchNostoCards(
        products,
        NostoRecommendationsSidecart.SECTION_NAME,
        NostoRecommendationsSidecart.MAX_PRODUCTS
      );
      if (valid.length === 0) return this.hide('all-cards-failed');

      const list = document.createElement('ul');
      list.className = 'nosto-sidecart__list';

      for (const { product, cardEl } of valid) {
        const li = document.createElement('li');
        li.className = 'nosto-sidecart__item';
        li.dataset.nostoProductId = product.product_id;
        li.appendChild(cardEl);
        list.appendChild(li);
      }

      const skeleton = this.querySelector('[data-nosto-skeleton]');
      if (skeleton) skeleton.remove();
      this.querySelectorAll(':scope > .nosto-sidecart__list').forEach((n) => n.remove());
      this.appendChild(list);
      this.wireCarousel(list);

      // Attribution Nosto des clics « carte → fiche produit » (après injection des cartes).
      attributeRecoClicks(list, this.placement);
    }

    // Flèches prev/next de la reco (maquette) : scroll horizontal + état disabled aux extrémités.
    wireCarousel(list) {
      const root = this.closest('[data-nosto-sidecart-root]');
      if (!root) return;
      const prev = root.querySelector('[data-nosto-prev]');
      const next = root.querySelector('[data-nosto-next]');
      if (!prev || !next) return;

      const step = () =>
        list.firstElementChild ? list.firstElementChild.getBoundingClientRect().width + 12 : list.clientWidth;
      const update = () => {
        const max = list.scrollWidth - list.clientWidth - 1;
        prev.toggleAttribute('disabled', list.scrollLeft <= 0);
        // clientWidth = 0 quand le drawer est fermé au 1er rendu : on préserve next
        // (le ResizeObserver rappellera update() dès que le drawer s'ouvre).
        next.toggleAttribute('disabled', list.clientWidth > 0 && list.scrollLeft >= max);
      };
      prev.addEventListener('click', () => list.scrollBy({ left: -step(), behavior: 'smooth' }));
      next.addEventListener('click', () => list.scrollBy({ left: step(), behavior: 'smooth' }));
      list.addEventListener('scroll', update, { passive: true });

      // Recalcule l'état des flèches quand la liste devient visible / change de taille
      // (cart drawer ouvert après coup, resize viewport, morph, etc.).
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(update);
        ro.observe(list);
      }
      update();
    }

    hide(reason) {
      this.setAttribute('data-nosto-empty', reason || '');
      const root = this.closest('[data-nosto-sidecart-root]');
      if (root) root.setAttribute('data-nosto-sidecart-empty', '');
    }
  }

  customElements.define('nosto-recommendations-sidecart', NostoRecommendationsSidecart);
}
